<template lang="pug">
  v-dialog(v-model="show" persistent max-width="1000px")
      ControlDialog(v-if="isNewTransactionDone" @complete="onCompleteTransaction" :newTransaction="newTransaction")
      ControlFailDialog(v-if="isNewTransactionNotProcessed"  @complete="onCompleteTransaction" :newTransaction="newTransaction" @openResolveDialog="openResolveDialog")
      v-form(v-if="!newTransaction" ref="form" v-model="valid"  @submit.prevent="onSubmitButtonClick" lazy-validation :disabled="saveInProgress" )
        v-card.d-flex.flex-column(flat)
          v-card-title {{ $t('bankTransactionPage.newTransaction') }}
          v-card-text
            v-row
              v-col(cols="6")
                v-text-field(outlined
                v-model="formattedIban"
                :label=" $t('bankTransactionPage.fromIbanColumnLabel')"
                :rules="ibanRules"
                @change="onFormattedIbanChange"
                )
              v-col(cols="6")
                v-text-field.amount(outlined
                v-model="amount"
                type="number"
                min="0"
                ng-disabled
                :label="$t('bankTransactionPage.amountColumnLabel')"
                :rules="amountRules"
                append-icon="fas fa-euro-sign"
                :hint= "$t('bankTransactionPage.hintAmountLabel')"
                )
            v-row
              v-col(cols="4")
                v-text-field(outlined
                v-model="variableSymbol"
                type="number"
                min="0"
                :label="$t('bankTransactionPage.variableSymbolColumnLabel')"
                :rules="variableSymbolRules"
                )
              v-col(cols="4")
                v-text-field(outlined
                v-model="specificSymbol"
                type="number"
                :label="$t('bankTransactionPage.specificSymbolColumnLabel')"
                )
              v-col(cols="4")
                v-text-field(outlined
                v-model="constantSymbol"
                type="number"
                :label="$t('bankTransactionPage.constantSymbol')"
                :rules="constantSymbolRules")
            v-row
              v-col(cols="6")
                v-menu(
                  v-model="menuDatePosted"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                  )
                  template(v-slot:activator="{ on , attrs }")
                    v-text-field(
                      outlined
                      v-model="formattedDatePosted"
                      :label="$t('bankTransactionPage.date1')"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      prepend-inner-icon="fas fa-calendar-alt"
                      @click:prepend-inner="menuDatePosted=true"
                      :hint="$t('bankTransactionPage.hintDatePostedLabel')")
                  v-date-picker(
                    v-model= "datePosted"
                    first-day-of-week="1"
                    no-title
                    scrollable
                    @input="onDatePostedInput"
                    prev-icon="fas fa-chevron-left"
                    next-icon="fas fa-chevron-right")
                    v-spacer
                    v-btn(text color="primary" @click="menuDatePosted = false") {{ $t('bankTransactionPage.closeDatePicker') }}
              v-col(cols="6")
                v-menu(
                  v-model="menuDateAvailable"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto")
                  template(v-slot:activator="{ on , attrs }")
                    v-text-field(outlined
                      v-model="formattedDateAvailable"
                      :label="$t('bankTransactionPage.date2')"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      prepend-inner-icon="fas fa-calendar-alt"
                      @click:prepend-inner="menuDateAvailable=true"
                      :hint="$t('bankTransactionPage.hintDateAvailableLabel')"
                      )
                  v-date-picker(
                    v-model= "dateAvailable"
                    first-day-of-week="1"
                    no-title
                    scrollable
                    @input="onDateAvailableInput"
                    prev-icon="fas fa-chevron-left"
                    next-icon="fas fa-chevron-right")
                    v-spacer
                    v-btn(text color="primary" @click="menuDateAvailable = false") {{ $t('bankTransactionPage.closeDatePicker') }}
            v-row
              v-col(cols="12")
                v-text-field(outlined
                v-model="reference"
                :label="$t('bankTransactionPage.descriptionColumnLabel')")
          v-divider
          v-card-actions
            v-spacer
            v-btn(color="primary" text  @click="onCloseButtonClick") {{ $t('addBoarderDialog.closeButtonLabel') }}
            v-btn(color="primary" type="submit" :disabled="!valid" :loading="saveInProgress") {{ $t('buttons.saveButtonLabel') }}

</template>

<script lang="ts">
import Vue from 'vue'
import moment from 'moment'
import {Component, Prop} from 'vue-property-decorator'
import {Action} from 'vuex-class'
import {CREATE_NEW_TRANSACTION} from '@/store/action-types'
import { NewBankTransactionPayload} from '@/types/payload'
import i18n from "@/i18n";
import ControlDialog from "@/components/user/ControlDialog.vue";
import ControlFailDialog from "@/components/user/ControlFailDialog.vue";
import BankTransaction from "@/model/BankTransaction/BankTransaction";

@Component({
  components: {
    ControlDialog,
    ControlFailDialog,
  },
})
export default class AddNewTransactionDialog extends Vue {
  $refs!: {
    form: Vue & {
      validate(): boolean
      resetValidation(): boolean
    }
  }
  @Prop({ type: Boolean, default: false }) readonly show!: boolean
  @Action(CREATE_NEW_TRANSACTION) createNewTransaction!: { (payload: NewBankTransactionPayload): Promise<BankTransaction> }

  private formattedIban: string | null = null
  private ibanRules: Array<(v: string) => string | boolean> = [
    (v: string) => !!v || i18n.t('validation.valueIsRequired').toString(),
    (v: string) => {
      const pattern = /^[A-Z]{2}[0-9\s]+$/gm
      return pattern.test(v) || i18n.t('validation.valueIsRequired').toString()
    },
    v => ( v && v.length >= 29) || i18n.t('bankTransactionPage.ibanValidationText').toString(),
    v => ( v && v.length <= 42 ) || i18n.t('bankTransactionPage.ibanValidationTextLabel').toString(),
  ]
  private amountRules: Array<(v: string) => string | boolean> = [
    (v: string) => !!v || i18n.t('validation.valueIsRequired').toString(),
    (v: string) => parseFloat(v) > 0 || i18n.t('validation.valueIsRequired').toString(),
    (v: string) => {
      const pattern = /^\d+(?:\.\d[0-9])?$/
      return pattern.test(v) || i18n.t('validation.valueIsRequired').toString()
    },
  ]
  private variableSymbolRules: Array<(v: string) => string | boolean> = [
    (v: string) => !!v || i18n.t('validation.valueIsRequired').toString(),
    (v: string) => parseFloat(v) > 0 || i18n.t('validation.valueIsRequired').toString(),
    (v: string) => {
      const pattern =  /^\d+$/
      return pattern.test(v) || i18n.t('validation.valueIsRequired').toString()
    }
  ]
  private constantSymbolRules: Array<(v: string) => string | boolean> = [
    (v: string) => !!v || i18n.t('validation.valueIsRequired').toString(),
    (v: string) => parseFloat(v) > 0 || i18n.t('validation.valueIsRequired').toString(),
    (v: string) => {
      const pattern =  /^\d+$/
      return pattern.test(v) || i18n.t('validation.valueIsRequired').toString()
    }
  ]
  private valid: boolean = true
  private saveInProgress: boolean = false
  private amount: string = ''
  private variableSymbol: string = ''
  private specificSymbol: string = ''
  private constantSymbol: string = '308'
  private datePosted: string = ''
  private dateAvailable: string = ''
  private formattedDatePosted: string = ''
  private formattedDateAvailable: string = ''
  private menuDatePosted: boolean = false
  private menuDateAvailable: boolean = false
  private newTransaction: BankTransaction | null = null
  private reference: string = ''


  get isNewTransactionDone (): boolean {
    return !!(this.newTransaction && this.newTransaction.transaction.status === 'done')
  }

  get isNewTransactionNotProcessed (): boolean {
    return !!(this.newTransaction && this.newTransaction.transaction.status !== 'done')
  }

  onFormattedIbanChange() {
    const iban: string = this.formattedIban!.replace(/\s/g, '')
    this.formattedIban = iban.replace(/\S{4}(?=.)/g, '$& ')
  }

  openResolveDialog(): void {
    this.$emit('openResolveDialog', this.newTransaction)
  }

  onCloseButtonClick () {
    this.$emit('close')
  }

  onDatePostedInput (date: string): void {
    this.formattedDatePosted = moment(date, 'YYYY-MM-DD').format('DD.MM.YYYY')
    this.menuDatePosted = false
  }

  onDateAvailableInput (date: string): void {
    this.formattedDateAvailable = moment(date, 'YYYY-MM-DD').format('DD.MM.YYYY')
    this.menuDateAvailable = false
  }

  onSubmitButtonClick () {
    if (this.$refs.form.validate()) {
    this.createTransaction()}
  }

  onCompleteTransaction (): void {
    this.$emit('complete', this.newTransaction)
  }
 async createTransaction() {
   this.saveInProgress = true
   const payload: NewBankTransactionPayload = {
     fromIban: this.formattedIban!.replace(/\s/g, ''),
     amount: Math.round(parseFloat(this.amount) * 100),

     variableSymbol: parseInt(this.variableSymbol),
     specificSymbol: this.specificSymbol ? parseInt(this.specificSymbol) : 0,
     constantSymbol: parseInt(this.constantSymbol),
     datePosted: this.datePosted,
     dateAvailable: this.dateAvailable,
     name: this.reference,
   }
    try {
      this.newTransaction = await this.createNewTransaction(payload)
      this.saveInProgress = false
    }
    catch (e) {

    }
    finally {
      this.saveInProgress = false
    }
 }

  mounted() {
  }
}

</script>

<style lang="stylus" scoped>
.card-body
  overflow auto

.day-status
  display flex
  border-radius 50%
  align-items center
  justify-content center
  height 30px
  width 30px
  background-color silver

.day-canceled
  color darkred !important

.event-icon
  font-size .875em !important

>>> input::-webkit-outer-spin-button
>>> input::-webkit-inner-spin-button
    -webkit-appearance none
    margin 0

.amount
  >>> .v-icon
    font-size 17px
    padding-bottom 5px
</style>
